import {
  SplitFactory,
  useSplitTreatments,
  useTreatments,
} from "@splitsoftware/splitio-react";
import React from "react";
import { useLocation } from "react-router-dom";

import { PageLoader } from "../components";
import { useConfig } from "./config";

interface SplitIOProps {
  children: JSX.Element | null;
}

export function SplitIOProvider(props: SplitIOProps) {
  const { children } = props;
  const config = useConfig();
  if (!config.splitioApiKey) {
    return <>{children}</>;
  }

  const sdkConfig: SplitIO.IBrowserSettings = {
    core: {
      authorizationKey: config.splitioApiKey,
      key: "generic",
    },
  };

  return (
    <SplitFactory config={sdkConfig}>
      {({ isReady }) => (isReady ? children : <PageLoader />)}
    </SplitFactory>
  );
}

function useFeatureFlag(name: string, partnerId: string, treatValue = "on") {
  const treatments = useTreatments([name], {
    partner_id: partnerId,
  });
  const location = useLocation();
  const defaultValue = sessionStorage.getItem(name);

  if (defaultValue) {
    return defaultValue === "false" ? false : true;
  }

  const searchParams = new URLSearchParams(location.search);
  const flagOnUrl = searchParams.get(name);

  if (flagOnUrl) {
    sessionStorage.setItem(name, `${treatValue === flagOnUrl}`);
    return flagOnUrl === treatValue;
  }

  return treatments[name].treatment === treatValue;
}

export function useDailyLocationGalleryFeatureFlag() {
  const treatments = useTreatments(["CH_6344_DAILY_LOCATION_GALLERY"]);

  return treatments["CH_6344_DAILY_LOCATION_GALLERY"].treatment === "on";
}

export function useMultiLocationGalleryFeatureFlag() {
  const treatments = useTreatments(["CH_6345_MULTI_LOCATION_GALLERY"]);

  return treatments["CH_6345_MULTI_LOCATION_GALLERY"].treatment === "on";
}

export function useWeworkPostPurchaseFeatureFlag() {
  const treatments = useTreatments(["CH_24736_WEWORK_POST_PURCHASE"]);

  return treatments["CH_24736_WEWORK_POST_PURCHASE"].treatment === "on";
}

export function useFMDisplayCatalogPage(partnerId: string) {
  const treatments = useTreatments(["CH15422_CM_CATALOG_PAGE"], {
    partner_id: partnerId,
  });

  return treatments["CH15422_CM_CATALOG_PAGE"].treatment === "on";
}

export function useFilteringByPayoutDate(partnerId: string) {
  const treatments = useTreatments(["CH17826_FILTER_ORDERS_BY_PAYOUT_DATE"], {
    partner_id: partnerId,
  });

  return treatments["CH17826_FILTER_ORDERS_BY_PAYOUT_DATE"].treatment === "on";
}

// Permanent feature flags

export function useFMSettingNewOrderByPartner(
  partnerId: string,
  userId: string
) {
  const treatments = useTreatments(["FM_SETTING_NEW_ORDER"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_SETTING_NEW_ORDER"].treatment === "on";
}

export function useFMCancelAccessBookingFF(partnerId: string) {
  const treatments = useTreatments(["FM_CANCEL_ACCESS_BOOKING"], {
    partner_id: partnerId,
  });

  return treatments["FM_CANCEL_ACCESS_BOOKING"].treatment === "on";
}

export function useFMDisplayingNavBarTabCalendar(partnerId: string) {
  const treatments = useTreatments(["FM_DISPLAYING_NAV_BAR_TAB_CALENDAR"], {
    partner_id: partnerId,
  });

  return treatments["FM_DISPLAYING_NAV_BAR_TAB_CALENDAR"].treatment === "on";
}

export function useFMDisplayingNavBarTabInventory(partnerId: string) {
  const treatments = useTreatments(["FM_DISPLAYING_NAV_BAR_TAB_INVENTORY"], {
    partner_id: partnerId,
  });

  return treatments["FM_DISPLAYING_NAV_BAR_TAB_INVENTORY"].treatment === "on";
}

export function useFMInventoryWithWindow(partnerId: string) {
  const treatments = useTreatments(["FM_INVENTORY_WITH_WINDOW"], {
    partner_id: partnerId,
  });

  return treatments["FM_INVENTORY_WITH_WINDOW"].treatment === "on";
}

export function useFMOrderCancellation(partnerId: string, userId: string) {
  const treatments = useTreatments(["FM_ORDER_CANCELLATION"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_ORDER_CANCELLATION"].treatment === "on";
}

export function useOfferingDailyAccessQuantityFieldFeatureFlag() {
  const treatments = useTreatments([
    "CH_10306_OFFERING_DAILY_ACCESS_QUANTITY_FIELD_FEATURE",
  ]);

  return (
    treatments["CH_10306_OFFERING_DAILY_ACCESS_QUANTITY_FIELD_FEATURE"]
      .treatment === "on"
  );
}

export function useFMMakePaymentDetailsOptional(partnerId: string) {
  const treatments = useTreatments(
    ["CH_10281_FM_MAKE_PAYMENT_DETAILS_OPTIONAL"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH_10281_FM_MAKE_PAYMENT_DETAILS_OPTIONAL"].treatment === "on"
  );
}

export function useFMDisplayManageBlockoutDates(partnerId: string) {
  const treatments = useTreatments(["EPIC_10138_MANAGE_BLOCKOUT_DATES"], {
    partner_id: partnerId,
  });

  return treatments["EPIC_10138_MANAGE_BLOCKOUT_DATES"].treatment === "on";
}

export function useFMOrderUpdates(partnerId: string, userId: string) {
  const treatments = useTreatments(["FM_ORDER_UPDATES"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_ORDER_UPDATES"].treatment === "on";
}

export function useAppleGooglePayEmbedInCheckoutPage(partnerId: string) {
  const treatments = useTreatments(
    ["SC23984_APPLE_GOOGLE_PAY_EMBED_IN_CHECKOUT_PAGE"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["SC23984_APPLE_GOOGLE_PAY_EMBED_IN_CHECKOUT_PAGE"].treatment ===
    "on"
  );
}

export function useSelfServePromotion(partnerId: string, userId: string) {
  const treatments = useTreatments(["CH20828_SELF_SERVED_PROMOTION"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["CH20828_SELF_SERVED_PROMOTION"].treatment === "on";
}

export function useViewDateByOrderCreatedDate(partnerId: string) {
  const treatments = useTreatments(
    ["CH_21972_VIEW_DATES_BY_ORDER_CREATED_DATE"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH_21972_VIEW_DATES_BY_ORDER_CREATED_DATE"].treatment === "on"
  );
}

export function useCampaignDiscountTypeAmount(partnerId: string) {
  const treatments = useTreatments(
    ["CH20828_CAMPAIGN_CREATE_DISCOUNT_AMOUNT"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH20828_CAMPAIGN_CREATE_DISCOUNT_AMOUNT"].treatment === "on"
  );
}

export function useDisplayNavCatalogs(partnerId: string, userId: string) {
  const treatments = useTreatments(["CH20084_DISPLAY_NAV_SPACE_EDITING"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["CH20084_DISPLAY_NAV_SPACE_EDITING"].treatment === "on";
}

export function usePriceDiscoverabilityFlag(partnerId: string) {
  return useFeatureFlag("CH_24421_DAYS_DISCOVERABILITY", partnerId);
}

export function useMinHourDiscoverabilityFlag(partnerId: string) {
  const treatments = useTreatments(["CH24423_MIN_HOUR_DISCOVERABILITY"], {
    partner_id: partnerId,
  });

  return treatments["CH24423_MIN_HOUR_DISCOVERABILITY"].treatment === "on";
}

export function useMembershipOfferingsFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH24437_MEMBERSHIP_OFFERINGS", partnerId);
}
export function useDisplayNavigatorMembershipsFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["DISPLAY_NAVIGATOR_MEMBERSHIPS"], {
    partner_id: partnerId,
  });

  return treatments["DISPLAY_NAVIGATOR_MEMBERSHIPS"].treatment === "on";
}

export function useMembershipPortalFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["CH25850_MEMBERSHIP_PORTAL"], {
    partner_id: partnerId,
  });

  return treatments["CH25850_MEMBERSHIP_PORTAL"].treatment === "on";
}

export function useTermedPromotionsFeatureFlag(partnerId: string) {
  const treatments = useTreatments(
    ["CH25310_TERMED_PROMOTIONS_FOR_MEMBERSHIPS"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH25310_TERMED_PROMOTIONS_FOR_MEMBERSHIPS"].treatment === "on"
  );
}

export function useMeetingRoomGalleryImprovement(partnerId: string) {
  const treatments = useTreatments(["CH26819_MEETING_ROOMS_GALLERY_LAYOUT"], {
    partner_id: partnerId,
  });

  return treatments["CH26819_MEETING_ROOMS_GALLERY_LAYOUT"].treatment === "on";
}

export function useDisplayMeetingRoomAvailableSpaces(partnerId: string) {
  const treatments = useTreatments(
    ["CH25785_DISPLAY_MEETING_ROOM_AVAILABLE_SPACES"],
    {
      partner_id: partnerId,
    }
  );

  return (
    treatments["CH25785_DISPLAY_MEETING_ROOM_AVAILABLE_SPACES"].treatment ===
    "on"
  );
}

export function useOnboardingPortalFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["CH27256_PORTAL_ONBOARDING"], {
    partner_id: partnerId,
  });

  return treatments["CH27256_PORTAL_ONBOARDING"].treatment === "on";
}

export function useBlockTemplateFeatureFlag(partnerId: string) {
  const treatments = useTreatments(["EPIC26304_CATALOG_LAYOUT_FLEXIBILITY"], {
    partner_id: partnerId,
  });

  return treatments["EPIC26304_CATALOG_LAYOUT_FLEXIBILITY"].treatment === "on";
}

export function useMeetingRoomDailyPricingFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH26976_MEETING_ROOM_DAILY_PRICING", partnerId);
}

export function useApplePayFixFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH28642_APPLE_PAY", partnerId);
}

export function useMeetingRoomHourExtensionFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH28555_MR_HOURS_EXTENSION", partnerId);
}

export function usePartnerOfferingSpacesFeatureFlag() {
  const treatments = useTreatments([
    "CH28662_USE_PARTNER_OFFERING_SPACES_HOOK",
  ]);

  return (
    treatments["CH28662_USE_PARTNER_OFFERING_SPACES_HOOK"].treatment === "on"
  );
}

export function useGalleryEmptyStateUpsell(partnerId: string) {
  return useFeatureFlag("CH29217_GALLERY_EMPTY_UPSELL", partnerId);
}

export function usePortalManageMembership(partnerId: string) {
  return useFeatureFlag("PORTAL_MANAGE_MEMBERSHIP", partnerId);
}

export function useMrMultiDayBookingFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH30942_MR_MULTI_DAY_BOOKING", partnerId);
}

export function useMrNonConsecutiveDateRangeFeatureFlag(partnerId: string) {
  return useFeatureFlag(
    "CH30942_MR_ALLOW_NON_CONSECUTIVE_DATE_RANGE",
    partnerId
  );
}

export function useShowMeetingRoomGalleryBanner(partnerId: string) {
  return useFeatureFlag("CH_31283_SHOW_MEETING_ROOM_GALLERY_BANNER", partnerId);
}

export function useHoverPreviewDateRangeFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH30942_HOVER_RREVIEW_DATE_RANGE", partnerId);
}

export function useTechArtistaDarkTheme() {
  const treatments = useTreatments(["TECHARTISTA_DARK_THEME"]);

  return treatments["TECHARTISTA_DARK_THEME"].treatment === "on";
}

export function useDisableSettingQuoteIDToCookies() {
  const treatments = useTreatments([
    "CH_30910_DISABLE_SETTING_QUOTE_ID_TO_COOKIE",
  ]);

  return (
    treatments["CH_30910_DISABLE_SETTING_QUOTE_ID_TO_COOKIE"].treatment === "on"
  );
}

export function useEnhanceCheckoutUX() {
  const treatments = useTreatments(["CH31752_ENHANCE_CHECKOUT_UX"]);

  return treatments["CH31752_ENHANCE_CHECKOUT_UX"].treatment === "on";
}

export function useStandartPickerOnMembershipFeatureFlag() {
  const treatments = useTreatments(["CH30908_STANDART_PICKER_ON_MEMBESHIP"]);

  return treatments["CH30908_STANDART_PICKER_ON_MEMBESHIP"].treatment === "on";
}

export function useBillingDetailsUpdate(partnerId: string) {
  return useFeatureFlag("CH31945_BILLING_DETAILS_UPDATE", partnerId);
}

export function useGoogleBookProductionEnvironmentFeatureFlag() {
  const treatments = useTreatments([
    "CH32387_GOOGLE_BOOK_PRODUCTION_ENVIRONMENT",
  ]);

  return (
    treatments["CH32387_GOOGLE_BOOK_PRODUCTION_ENVIRONMENT"].treatment === "on"
  );
}

export function useEntryBasedMembershipFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_32982_ENTRY_BASED_MEMBERSHIP", partnerId);
}

export function useDisplayMerchantNameAndAddress() {
  const treatments = useTreatments([
    "CH_33395_DISPLAY_MERCHANT_NAME_AND_ADRESS",
  ]);

  return (
    treatments["CH_33395_DISPLAY_MERCHANT_NAME_AND_ADRESS"].treatment === "on"
  );
}

export function useCMDashboardTransactionsFeatureFlag() {
  const treatments = useTreatments(["CH_34023_CM_DASHBOARD_TRANSACTIONS"]);

  return treatments["CH_34023_CM_DASHBOARD_TRANSACTIONS"].treatment === "on";
}

export function useIntelligentOfficePromoFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_33916_INTELLIGENT_OFFICE_PROMO", partnerId);
}

export function useDisplaySavingAmountFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH34411_DISPLAY_SAVING_AMOUNT"],
  });

  return treatments["CH34411_DISPLAY_SAVING_AMOUNT"].treatment === "on";
}

export function useServiceMarketingPackageFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH_34716__SERVICE_MARKETING_PACKAGE", partnerId);
}

export function useInitializeOneSignalFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_34716_INITIALIZE_ONE_SIGNAL"],
  });

  return treatments["CH_34716_INITIALIZE_ONE_SIGNAL"].treatment === "on";
}

export function useFMShowReportsPayoutFeatureFlag(
  partnerId: string,
  userId: string
) {
  const treatments = useTreatments(["FM_SHOW_REPORTS_PAYOUT"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_SHOW_REPORTS_PAYOUT"].treatment === "on";
}

export function useFMShowMarketingReportFeatureFlag(
  partnerId: string,
  userId: string
) {
  const treatments = useTreatments(["FM_SHOW_MARKETING_REPORT"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["FM_SHOW_MARKETING_REPORT"].treatment === "on";
}

export function useFMShowPerformanceReportFeatureFlag(
  partnerId: string,
  userId: string
) {
  const treatments = useTreatments(["CH_39882_FM_SHOW_PERFORMANCE_REPORT"], {
    partner_id: partnerId,
    user_id: userId,
  });

  return treatments["CH_39882_FM_SHOW_PERFORMANCE_REPORT"].treatment === "on";
}

export function useRefactorPaymentHookFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH35534_REFACTOR_PAYMENT_HOOK"],
  });

  return treatments["CH35534_REFACTOR_PAYMENT_HOOK"].treatment === "on";
}

export function useMrGalleryImprovementsFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_35968_MR_GALLERY_IMPROVEMENTS"],
  });

  return treatments["CH_35968_MR_GALLERY_IMPROVEMENTS"].treatment === "on";
}

export function useTheSetCollectiveDarkTheme() {
  const treatments = useTreatments(["THE_SET_COLLECTIVE_DARK_THEME"]);

  return treatments["THE_SET_COLLECTIVE_DARK_THEME"].treatment === "on";
}

export function useMrGalleryImprovementsV2FeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["MR_GALLERY_IMPROVEMENTS"],
  });

  return treatments["MR_GALLERY_IMPROVEMENTS"].treatment === "on";
}

export function useLocationDropdownFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH35969_LOCATION_DROPDOWN"],
  });

  return treatments["CH35969_LOCATION_DROPDOWN"].treatment === "on";
}

export function useDurationPickerFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH37246_DURATION_PICKER"],
  });

  return treatments["CH37246_DURATION_PICKER"].treatment === "on";
}

export function useIncludeAllPaymentsFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH37148_INCLUDE_ALL_PAYMENTS"],
  });
  return treatments["CH37148_INCLUDE_ALL_PAYMENTS"].treatment === "on";
}

export function usePaymentModalFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH37148_PAYMENT_MODAL"],
  });
  return treatments["CH37148_PAYMENT_MODAL"].treatment === "on";
}

export function usePriceBreakdownPerHourFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH37247_PRICE_BREAKDOWN_PER_HOUR"],
  });
  return treatments["CH37247_PRICE_BREAKDOWN_PER_HOUR"].treatment === "on";
}

export function useSpaceDescriptionEditFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_37609_SPACE_DESCRIPTION_EDIT"],
  });
  return treatments["CH_37609_SPACE_DESCRIPTION_EDIT"].treatment === "on";
}

export function usePortalAuthFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_31549_PORTAL_AUTH"],
  });
  return treatments["CH_31549_PORTAL_AUTH"].treatment === "on";
}

export function useDiscountPopupFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH38414_DISCOUNT_POPUP"],
  });
  return treatments["CH38414_DISCOUNT_POPUP"].treatment === "on";
}

export function useCatalogLocationFilterFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_38448_CATALOG_LOCATION_FILTER"],
  });
  return treatments["CH_38448_CATALOG_LOCATION_FILTER"].treatment === "on";
}

export function useMrGalleryDateTimeFilterFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH38452_DATE_TIME_FILTER"],
  });
  return treatments["CH38452_DATE_TIME_FILTER"].treatment === "on";
}

export function useCapacityFilterFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_38451_CAPACITY_FILTER_FEATURE_FLAG"],
  });
  return treatments["CH_38451_CAPACITY_FILTER_FEATURE_FLAG"].treatment === "on";
}

export function useCMDashboardSelfServeImageFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH37964_CM_DASHBOARD_SELF_SERVE_IMAGE", partnerId);
}

export function useStaleCheckoutFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_39304_STALE_CHECKOUT_FEATURE_FLAG"],
  });
  return treatments["CH_39304_STALE_CHECKOUT_FEATURE_FLAG"].treatment === "on";
}

export function useDateTimeDFilterFullscreenFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH39679_DATE_TIME_FILTER_FULLSCREEN"],
  });
  return treatments["CH39679_DATE_TIME_FILTER_FULLSCREEN"].treatment === "on";
}

export function useGolfSimulationAccessPortalFeatureFlag(partnerId: string) {
  return useFeatureFlag("CH40266_ONE_STATE_ST_PORTAL_MEMBER_GOLF", partnerId);
}

export function useValidateEmailOnSubmitOrderFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["SC34585_VALIDATE_EMAIL_ON_SUBMIT_ORDER"],
  });
  return (
    treatments["SC34585_VALIDATE_EMAIL_ON_SUBMIT_ORDER"].treatment === "on"
  );
}

export function useCMSuperAdminFeatureFlag() {
  const { treatments } = useSplitTreatments({
    names: ["CH_41287_CM_SUPER_ADMIN"],
  });
  return treatments["CH_41287_CM_SUPER_ADMIN"].treatment === "on";
}

export function useModifyMrHourlyBookingPhase1TimeFeatureFlag(
  partnerId: string
) {
  return useFeatureFlag("MODIFY_MR_HOURLY_BOOKING_PHASE_1_TIME", partnerId);
}
